import { Link } from "react-router-dom";
import useNoIndex from "@/hooks/useNoIndex";

export default function PrivacyPolicyPage() {
  useNoIndex();

  const lastUpdated = "May 26, 2026";

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-12 md:py-16">
        <Link to="/" className="text-sm text-teal-600 hover:text-teal-700">
          ← Back to home
        </Link>

        <h1 className="text-4xl md:text-5xl font-bold mt-4 mb-3">Privacy Policy</h1>
        <p className="text-slate-600 mb-10">Last updated: {lastUpdated}</p>

        <div className="space-y-8 text-slate-700 leading-7">
          <section>
            <p>
              Welcome to CreaVideos.online (“Website”), operated by CreaVideos – Renaud Jean Noel
              Pomies (“CreaVideos”, “we”, “our”, or “us”).
            </p>
            <p className="mt-3">
              This Privacy Policy explains how we collect, use, disclose, and protect your personal
              data when you use our Website and services.
            </p>
            <p className="mt-3">
              We comply with the General Data Protection Regulation (GDPR) and other applicable data
              protection laws.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">1. Data Controller</h2>
            <p>The data controller responsible for your personal data is:</p>
            <p className="mt-2">CreaVideos – Renaud Jean Noel Pomies</p>
            <p>
              Email: <a className="text-teal-600 hover:text-teal-700" href="mailto:reno@creavideos.online">reno@creavideos.online</a>
            </p>
            <p>
              Website: <a className="text-teal-600 hover:text-teal-700" href="https://www.creavideos.online">https://www.creavideos.online</a>
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">2. Personal Data We Collect</h2>
            <p>We may collect the following categories of personal data:</p>

            <h3 className="text-xl font-semibold text-slate-900 mt-4 mb-2">2.1 Account Information</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Name</li>
              <li>Email address</li>
              <li>Password (encrypted)</li>
              <li>Account settings</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-900 mt-4 mb-2">2.2 Payment Information</h3>
            <p>Payments are processed via PayPal.</p>
            <p className="mt-2">We do not store full payment details.</p>
            <p className="mt-2">We may receive:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>payment confirmation</li>
              <li>subscription status</li>
              <li>transaction ID</li>
              <li>billing-related metadata</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-900 mt-4 mb-2">2.3 User Content</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Text entered into templates</li>
              <li>Images or videos uploaded</li>
              <li>Generated videos</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-900 mt-4 mb-2">2.4 Usage Data</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>IP address</li>
              <li>browser type</li>
              <li>device information</li>
              <li>pages visited</li>
              <li>usage behavior</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-900 mt-4 mb-2">2.5 Meta (Facebook / Instagram) Data</h3>
            <p>If you connect your account:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>name</li>
              <li>email</li>
              <li>profile picture</li>
              <li>account ID</li>
              <li>permissions granted</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">3. How We Use Your Data</h2>
            <p>We use your data to:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>provide and operate the service</li>
              <li>create and manage your account</li>
              <li>process subscriptions and payments</li>
              <li>allow video creation and rendering</li>
              <li>enable social media sharing</li>
              <li>communicate with you</li>
              <li>improve performance and features</li>
              <li>comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">4. Legal Basis (GDPR)</h2>
            <p>We process your data under:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>Contract → to provide the service</li>
              <li>Legitimate interest → to improve and secure the platform</li>
              <li>Consent → for optional features (e.g. Meta login)</li>
              <li>Legal obligation → accounting, taxes</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">5. Facebook / Instagram (Meta)</h2>
            <p>If you connect via Meta:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>we only access data you approve</li>
              <li>we use it for login and sharing</li>
              <li>we never post without your action</li>
            </ul>
            <p className="mt-3">
              To enable these features, we may store limited Meta-related connection data such as account IDs,
              usernames, granted permission status, and access tokens needed to perform user-initiated sharing.
            </p>
            <p className="mt-3">
              We disclose Meta-related data only to Meta Platforms as required to authenticate your account and
              process your explicit sharing requests. We do not sell this data or use it for independent advertising.
            </p>
            <p className="mt-2">You can disconnect anytime via:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>your account settings</li>
              <li>Meta settings</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">6. Third-Party Services</h2>
            <p>We use trusted third-party providers:</p>

            <h3 className="text-xl font-semibold text-slate-900 mt-4 mb-2">6.1 PayPal (Payments)</h3>
            <p>Used to process subscriptions securely.</p>
            <p className="mt-2">PayPal acts as an independent data controller.</p>
            <p className="mt-2">Their privacy policy applies to payment data.</p>

            <h3 className="text-xl font-semibold text-slate-900 mt-4 mb-2">6.2 PixVerse (AI Video Generation)</h3>
            <p>We use PixVerse to generate videos from images.</p>
            <p className="mt-2">This means:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>images and prompts you submit may be processed by PixVerse</li>
              <li>processing is necessary to deliver the service</li>
            </ul>
            <p className="mt-2">We only send required data.</p>

            <h3 className="text-xl font-semibold text-slate-900 mt-4 mb-2">6.3 Google / YouTube (OAuth and YouTube upload)</h3>
            <p>
              If you connect YouTube, we use Google OAuth and YouTube API services to authenticate your account
              and upload videos only when you request sharing.
            </p>
            <p className="mt-2">For this integration:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>we may receive your Google account basic profile/channel information and OAuth tokens</li>
              <li>we use this data only to maintain the connection and perform user-initiated YouTube uploads</li>
              <li>we do not transfer Google user data to third parties except as required to provide this feature</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-900 mt-4 mb-2">6.4 TikTok (OAuth and upload-to-TikTok flow)</h3>
            <p>
              If you connect TikTok, we use TikTok OAuth and upload APIs to send your rendered video to TikTok
              when you choose to share.
            </p>
            <p className="mt-2">
              Posting is user-controlled: after upload, you may need to finish publishing inside TikTok.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">7. Data Retention</h2>
            <p>We keep your data:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>while your account is active</li>
              <li>as needed for legal obligations (e.g. accounting)</li>
            </ul>
            <p className="mt-2">You may request deletion at any time.</p>

            <section id="data-deletion-instructions" className="mt-4">
              <h3 className="text-xl font-semibold text-slate-900 mb-2">7.1 Data deletion instructions</h3>
              <p>If you want CreaVideos to delete your data, please follow these steps:</p>
              <ol className="list-decimal pl-6 space-y-2 mt-2">
                <li>
                  Send an email to <a className="text-teal-600 hover:text-teal-700" href="mailto:reno@creavideos.online">reno@creavideos.online</a> with
                  the subject line "Data Deletion Request".
                </li>
                <li>
                  Include the email address linked to your CreaVideos account and, if applicable,
                  your Facebook/Instagram account ID.
                </li>
                <li>
                  We may ask for verification to confirm account ownership before processing your
                  request.
                </li>
              </ol>
              <p className="mt-3">
                After verification, we will delete or anonymize eligible personal data within a
                reasonable period, except for information we must keep for legal, fraud-prevention,
                or accounting purposes.
              </p>
            </section>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">8. Your Rights (GDPR)</h2>
            <p>You have the right to:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>access your data</li>
              <li>correct your data</li>
              <li>delete your data (“right to be forgotten”)</li>
              <li>restrict processing</li>
              <li>object to processing</li>
              <li>data portability</li>
            </ul>
            <p className="mt-2">To exercise your rights:</p>
            <p>
              <a className="text-teal-600 hover:text-teal-700" href="mailto:reno@creavideos.online">reno@creavideos.online</a>
            </p>
            <p className="mt-2">
              You also have the right to file a complaint with a supervisory authority in the EU.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">9. Data Security</h2>
            <p>We implement appropriate security measures to protect your data.</p>
            <p className="mt-2">However, no system is 100% secure.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">10. International Transfers</h2>
            <p>Your data may be processed outside the EU (e.g. PayPal, PixVerse).</p>
            <p className="mt-2">
              When this happens, we ensure appropriate safeguards (e.g. standard contractual clauses).
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">11. Cookies and Similar Technologies</h2>
            <p>
              CreaVideos uses cookies and similar technologies to ensure the proper
              functioning of the Website and to provide essential features.
            </p>

            <h3 className="text-xl font-semibold text-slate-900 mt-4 mb-2">What Are Cookies?</h3>
            <p>
              Cookies are small text files stored on your device when you visit a
              website. They help websites function correctly and improve user
              experience.
            </p>

            <h3 className="text-xl font-semibold text-slate-900 mt-4 mb-2">How We Use Cookies</h3>
            <p>
              We currently use only cookies and similar technologies that are
              strictly necessary for the operation of the Website.
            </p>
            <p className="mt-3">These include:</p>

            <h4 className="text-lg font-semibold text-slate-900 mt-4 mb-2">Authentication and session cookies</h4>
            <p>
              To allow you to log in, stay logged in, and securely access your
              account.
            </p>

            <h4 className="text-lg font-semibold text-slate-900 mt-4 mb-2">Security cookies</h4>
            <p>
              To protect against fraudulent activity and ensure the security of the
              Website.
            </p>

            <h4 className="text-lg font-semibold text-slate-900 mt-4 mb-2">Payment-related functionality</h4>
            <p>
              To enable secure payment processing through our payment provider
              (PayPal).
            </p>

            <h4 className="text-lg font-semibold text-slate-900 mt-4 mb-2">Social media integration</h4>
            <p>
              To allow you to connect your account and share videos to platforms
              such as Facebook and Instagram when you choose to do so.
            </p>

            <p className="mt-3">
              These cookies are essential for providing the services you request and
              cannot be disabled without affecting the functionality of the Website.
            </p>

            <h3 className="text-xl font-semibold text-slate-900 mt-4 mb-2">No Analytics or Marketing Cookies</h3>
            <p>
              CreaVideos does not currently use analytics, advertising, or tracking
              cookies.
            </p>
            <p className="mt-3">
              We do not track your browsing behavior for marketing purposes and do
              not use third-party tracking tools such as advertising pixels.
            </p>

            <h3 className="text-xl font-semibold text-slate-900 mt-4 mb-2">Third-Party Services</h3>
            <p>Some essential features rely on trusted third-party providers, including:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>PayPal for payment processing</li>
              <li>Meta Platforms (Facebook / Instagram) for login and sharing features</li>
            </ul>
            <p className="mt-3">
              These providers may use their own cookies or similar technologies as
              part of the services they provide. Their use of data is governed by
              their respective privacy policies.
            </p>

            <h3 className="text-xl font-semibold text-slate-900 mt-4 mb-2">Changes to This Section</h3>
            <p>
              If we introduce analytics or marketing cookies in the future, we will
              update this section and implement a cookie consent mechanism allowing
              you to manage your preferences.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">12. Changes to This Policy</h2>
            <p>We may update this Privacy Policy.</p>
            <p className="mt-2">Continued use = acceptance.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">13. Contact</h2>
            <p>CreaVideos</p>
            <p>
              Email: <a className="text-teal-600 hover:text-teal-700" href="mailto:reno@creavideos.online">reno@creavideos.online</a>
            </p>
            <p>
              Website: <a className="text-teal-600 hover:text-teal-700" href="https://www.creavideos.online">https://www.creavideos.online</a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
